import React, { useEffect, useState } from "react";
import { deletePerson, getPersons, searchPersons, updatePerson } from "../service/personService";
import { Person } from "../types";

export function useListPersons() {
  const [persons, setPersons] = useState<Person[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);

  const fetchPersons = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getPersons(page, rowsPerPage);
      setPersons(response.content);
      setTotalElements(response.totalElements);
    } catch (e: any) {
      setError(e.message || "Failed to fetch persons");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!searching) fetchPersons();
  }, [page, rowsPerPage, searching]);

  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const search = async (name: string, mobileNo: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await searchPersons(name, mobileNo);
      setPersons(response.content);
      setTotalElements(response.content.length);
      setSearching(true);
    } catch (e: any) {
      setError(e.message || "Failed to search persons");
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setSearching(false);
    setPage(0);
  };

  const update = async (externalId: number, changedFields: Partial<Person>) => {
    const updated = await updatePerson(externalId, changedFields);
    setPersons((prev) => prev.map((person) => (person.externalId === externalId ? { ...person, ...updated } : person)));
  };

  const remove = async (externalId: number) => {
    await deletePerson(externalId);
    setPersons((prev) => prev.filter((person) => person.externalId !== externalId));
    setTotalElements((prev) => Math.max(0, prev - 1));
  };

  return {
    persons,
    loading,
    error,
    page,
    rowsPerPage,
    totalElements,
    searching,
    handleChangePage,
    handleChangeRowsPerPage,
    reload: fetchPersons,
    actions: { search, clearSearch, update, remove },
  };
}
